import { useState } from "react";
import { ChevronDown, Plus, Sprout, Check, Loader2 } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGardens } from "@/contexts/GardenContext";
import { createGarden, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { upgradeMessageFrom } from "@/components/UpgradeBanner";

/**
 * Sidebar widget letting the user pick the active garden, or create a new
 * one. The active garden id is persisted via GardenContext (localStorage).
 */
export default function GardenSwitcher({ compact = false }) {
    const { gardens, activeGarden, setActiveGarden, refresh, loading } = useGardens();
    const [createOpen, setCreateOpen] = useState(false);
    const [name, setName] = useState("");
    const [creating, setCreating] = useState(false);

    const handleCreate = async (e) => {
        e?.preventDefault();
        if (!name.trim()) return;
        setCreating(true);
        try {
            const garden = await createGarden({ name: name.trim() });
            await refresh();
            setActiveGarden(garden.id);
            toast.success(`'${garden.name}' created`);
            setCreateOpen(false);
            setName("");
        } catch (err) {
            const upgradeMsg = upgradeMessageFrom(err);
            if (upgradeMsg) {
                toast.error(upgradeMsg, {
                    action: {
                        label: "Upgrade",
                        onClick: () => window.location.assign("/pricing"),
                    },
                });
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setCreating(false);
        }
    };

    if (loading && !activeGarden) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-[var(--vm-text-muted)]" data-testid="garden-switcher-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> loading gardens…
            </div>
        );
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        data-testid="garden-switcher-trigger"
                        className="group w-full flex items-center gap-2 px-3 py-2.5 rounded-md border border-[var(--vm-border)] bg-[var(--vm-bg)]/50 hover:bg-[var(--vm-card)] transition text-left"
                    >
                        <Sprout className="w-4 h-4 text-[var(--vm-accent)] shrink-0" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                            {!compact && (
                                <div className="overline text-[10px] text-[var(--vm-sage)] mb-0.5">
                                    Active garden
                                </div>
                            )}
                            <div className="text-sm text-[var(--vm-text)] truncate font-medium">
                                {activeGarden?.name || "No garden"}
                            </div>
                        </div>
                        <ChevronDown className="w-3.5 h-3.5 text-[var(--vm-text-muted)] group-hover:text-[var(--vm-text)] transition" />
                    </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="start" className="w-60" data-testid="garden-switcher-menu">
                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">
                        Your gardens
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {gardens.length === 0 && (
                        <div className="px-2 py-3 text-xs italic text-[var(--vm-text-muted)]">
                            No gardens yet. Create your first one below.
                        </div>
                    )}
                    {gardens.map((g) => (
                        <DropdownMenuItem
                            key={g.id}
                            data-testid={`garden-switcher-option-${g.id}`}
                            onClick={() => setActiveGarden(g.id)}
                            className={g.id === activeGarden?.id ? "bg-[var(--vm-accent)]/15" : ""}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-[var(--vm-text)] truncate">{g.name}</div>
                                <div className="text-[10px] text-[var(--vm-text-muted)] truncate">
                                    {g.location || "—"}
                                </div>
                            </div>
                            {g.id === activeGarden?.id && (
                                <Check className="w-4 h-4 text-[var(--vm-accent)] shrink-0 ml-2" />
                            )}
                        </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        data-testid="garden-switcher-add"
                        onClick={() => setCreateOpen(true)}
                        className="text-[var(--vm-accent)] hover:text-[var(--vm-accent-hover)]"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New garden
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-[var(--vm-card)] border-[var(--vm-border)]" data-testid="new-garden-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif-display text-2xl">New garden</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="grid gap-4 py-2">
                        <div className="grid gap-2">
                            <Label htmlFor="new-garden-name">Garden name</Label>
                            <Input
                                id="new-garden-name"
                                data-testid="new-garden-name-input"
                                autoFocus
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Allotment 12, Roof balcony, Polytunnel garden"
                            />
                            <p className="text-[11px] text-[var(--vm-text-muted)]">
                                You can edit location, climate and dimensions later from the Garden Map.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={creating || !name.trim()}
                                data-testid="new-garden-submit"
                                className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                            >
                                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                                Create
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
